import {forwardRef, useEffect, useImperativeHandle, useMemo, useState} from 'react';
import {IServiceRequest} from '../../../../../shared/types/serviceRequest.types';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import {useEventBus} from '../../../../../hooks/useEventBus';
import {serviceRequestsQueryTrigger} from '../../../../../store/service-requests/atoms';
import {useSetRecoilState} from 'recoil';
import {useForm} from 'react-hook-form';
import {ServiceRequestService} from '../../../../../api/services/serviceRequest.service';
import {toast} from 'react-toastify';
import {AsyncSelectController} from '../../../../AsyncSelectController/AsyncSelectController';
import {UserAccountDto} from '../../../../../global/api';
import {IUserAccount, TypeAccountRoles} from '../../../../../shared/types/user-account.types';
import {useSearchAccounts} from '../../../../../hooks/useSearchAccounts';

import {editWorkersFormStyles as styles} from './EditWorkersFormModal.styles';
import {AxiosResponse} from 'axios';

interface Props {
  serviceRequest?: IServiceRequest;
}

export interface EditWorkersFormModalHandlers {
  handleClickOpen: () => void;
}

type UserAccount = IUserAccount | UserAccountDto;

interface IEditWorkersForm {
  workers: Array<UserAccount>;
}

function prepareDefaultValues(serviceRequest?: IServiceRequest): IEditWorkersForm {
  return {
    workers: (serviceRequest?.workers ?? []) as Array<IUserAccount | UserAccountDto>,
  };
}

export const EditWorkersFormModal = forwardRef<EditWorkersFormModalHandlers, Props>(({serviceRequest}, ref) => {
  const [open, setOpen] = useState(false);
  const bus = useEventBus();

  const defaultValues = useMemo(() => prepareDefaultValues(serviceRequest), [serviceRequest]);
  const triggerSRQuery = useSetRecoilState(serviceRequestsQueryTrigger);
  const {handleSearch, hasNextPage, fetchNextPage, data, searchTerm, searchLoading} = useSearchAccounts({
    fullSize: false,
    additionalFilter: {
      roles: TypeAccountRoles.WORKER as unknown as TypeAccountRoles[],
      isActive: true,
    },
  });

  const form = useForm<IEditWorkersForm>({
    defaultValues,
  });

  const {control, handleSubmit, reset, setValue, watch} = form;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useImperativeHandle(ref, () => ({
    handleClickOpen: () => {
      setOpen(true);
      if (searchTerm) {
        handleSearch('');
      }
    },
  }));

  const watchedWorkers = watch('workers');

  const sameWorkers = useMemo(() => {
    const initialWorkers = serviceRequest?.workers ?? [];
    const currentWorkers = watchedWorkers ?? [];

    if (initialWorkers.length !== currentWorkers.length) {
      return false;
    }

    const initialUids = initialWorkers.map((worker) => worker.uid).sort();
    const currentUids = currentWorkers.map((worker) => worker?.uid).sort();

    return initialUids.every((uid, index) => uid === currentUids[index]);
  }, [serviceRequest?.workers, watchedWorkers]);

  const handleClose = () => {
    setOpen(false);
    reset(defaultValues);
  };

  const handleChangeSelect = (_name: string, newValue: UserAccount | UserAccount[]) => {
    if (Array.isArray(newValue)) {
      const uniqueArr = newValue.reduce((acc, curr) => {
        if (acc.find((item) => item.uid === curr.uid)) {
          return acc;
        }
        acc.push(curr);
        return acc;
      }, [] as UserAccount[]);
      setValue('workers', uniqueArr);
      return;
    }
    setValue('workers', [newValue]);
  };

  const handleSave = handleSubmit(async (data) => {
    try {
      if (!serviceRequest) {
        return;
      }

      const workersUids: string[] = data.workers.reduce((acc, curr) => {
        if (curr.uid) {
          acc.push(curr.uid);
        }
        return acc;
      }, [] as string[]);

      let res: AxiosResponse;
      if (!workersUids.length) {
        res = await ServiceRequestService.clearExecutor(serviceRequest.id);
      } else {
        res = await ServiceRequestService.setWorkers([serviceRequest.id], workersUids);
      }

      if (res.status) {
        toast.success('Исполнители успешно обновлены', {
          autoClose: 2000,
          hideProgressBar: true,
          pauseOnHover: false,
          draggable: false,
        });
      }
      setTimeout(() => triggerSRQuery((v) => v + 1), 1000);
    } catch (e) {
      bus?.emit('error', [e]);
    } finally {
      handleClose();
    }
  });

  const handleInputChange = (inputValue: string) => {
    handleSearch(inputValue);
  };

  const handleScroll = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" sx={styles.container}>
      <DialogTitle>Исполнители</DialogTitle>
      <DialogContent>
        <Stack mt={2}>
          <AsyncSelectController<UserAccount>
            control={control}
            name="workers"
            multiple
            onChange={handleChangeSelect}
            inputValue={searchTerm}
            onInputChange={handleInputChange}
            onScroll={handleScroll}
            data={data}
            loading={searchLoading}
            getOptionLabel={(option) => {
              return option ? `${option.firstName} ${option.patronymic} ${option.lastName}` : '';
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={sameWorkers}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
});
